import Vue from 'vue';
import _ from "lodash";
import portfolioAPI from '../api/portfolio';
import i18n from '../../i18n';
import stockAPI from '../api/stock';
import chartAPI from "../api/chart";
import common from '../../libs/common';
import { ADD_TOAST_MESSAGE } from "vuex-toast";

const types = {
    LOADING: 'LOADING',
    LOADING_BY_KEY: 'LOADING_BY_KEY',
    GET_DETAIL_OK: 'GET_DETAIL_OK',
    GET_DETAIL_FAILED: 'GET_DETAIL_FAILED',
    BUY_STOCK_OK: 'BUY_STOCK_OK',
    BUY_STOCK_FAIL: 'BUY_STOCK_FAIL',
    SELL_STOCK_OK: 'SELL_STOCK_OK',
    SELL_STOCK_FAIL: 'SELL_STOCK_FAIL',
    GET_PORTFOLIOS_OK: 'GET_PORTFOLIOS_OK',
    GET_PORTFOLIOS_FAILED: 'GET_PORTFOLIOS_FAILED',
    GET_PRICE_HISTORY_OK: 'GET_PRICE_HISTORY_OK',
    GET_PRICE_HISTORY_FAIL: 'GET_PRICE_HISTORY_FAIL',
    SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
    SET_PAST_PRICE: 'SET_PAST_PRICE',
    GET_PERFORMANCE_CHART_OK: 'GET_PERFORMANCE_CHART_OK',
}

const initState = {
    loading: false,
    form: {},
    portfolio: {},
    vplist: {},
    investedOK: false,
    soldOK: false,
    pastPrice: null,
    performanceChart: {
        series: [
            {
                name: 'Index',
                data: []
            },
            {
                name: 'VNIndex',
                data: []
            },
            {
                name: 'VN30',
                data: []
            }
        ],
        chartOptions: {
            chart: {
                width: 350,
                type: 'line',
                id: 'performanceChart',
                animations: {
                    enabled: false
                }
            },
            annotations: {

            },
            colors: ['var(--purple)', 'var(--yellow)', 'var(--green)'],
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: 'smooth'
            },
            grid: {
                show: false
            },
            xaxis: {
                type: 'category',
                labels: {
                    formatter: function (timestamp) {
                        var formattedDate = common.formatDate(timestamp);
                        return formattedDate;
                    }
                }
            },
            yaxis: {
                labels: {
                    formatter: function (value) {
                        return (value * 100).toFixed(1) + "%";
                    }
                },
                tooltip: {
                    enabled: true
                }
            },
        }
    },
    activeTab: {
        id: 'watchlist',
        name: "Watchlist",
        mode: 'expiriment',
        noOfSymbols: 0
    },
}

const getters = {
    form: state => state.form,
    loading: state => state.loading,
    portfolio: state => state.portfolio,
    investedOK: state => state.investedOK,
    vplist: state => state.vplist,
    pastPrice: state => state.pastPrice,
    activeTab: state => state.activeTab,
    performanceChart: state => state.performanceChart
}

const actions = {
    async getPerformanceChart({ commit, dispatch }, params) {
        try {
            commit('LOADING_BY_KEY', { key: "performanceChartLoading", status: true });
            const { data } = await chartAPI(dispatch).getPerformanceChart(params.pid, params.period);
            commit(types.GET_PERFORMANCE_CHART_OK, data);
        } catch (e) {
            Vue.$log.error("Get price history failed", e);
            commit('LOADING_BY_KEY', { key: "performanceChartLoading", status: false });
        }
    },

    async getPriceHistory({ commit, dispatch }, params) {
        try {
            const { data } = await stockAPI(dispatch).getPriceHistory(params.code, params.date);
            if (data && data.price > 0) {
                commit(types.GET_PRICE_HISTORY_OK, data);
            } else {
                commit(types.GET_PRICE_HISTORY_FAIL);
            }
        } catch (e) {
            Vue.$log.error("Get price history failed", e);
        }
    },

    async createPortfolio({ commit, dispatch }, params) {
        try {
            const { data } = await portfolioAPI(dispatch).create(params);
            if (data && data.code === 200) {
                const pid = data.items[0];
                dispatch("getPortfolios", pid);
                dispatch("getPortfolioDetail", pid);
            } else {
                dispatch(
                    ADD_TOAST_MESSAGE,
                    { text: data.message, type: 'danger' },
                    { root: true }
                );
            }
        } catch (e) {
            Vue.$log.error("Failed to create the portfolio", e);
        }
    },

    async removePortfolio({ commit, dispatch }, pid) {
        try {
            await portfolioAPI(dispatch).remove(pid);
            // set active to watchlist when remove current pid
            dispatch("getPortfolios", 'watchlist');
            dispatch(
                ADD_TOAST_MESSAGE,
                { text: i18n.t('messages.portfolio_remove_ok'), type: 'success' },
                { root: true }
            );
        } catch (e) {
            Vue.$log.error("Failed to remove the portfolio", e);
        }
    },

    async getPortfolios({ commit, dispatch }, pid) {
        try {
            commit('LOADING_BY_KEY', { key: "portfolioListLoading", status: true });
            const { data } = await portfolioAPI(dispatch).getList();
            if (pid) {
                data['pid'] = pid;
            }
            commit(types.GET_PORTFOLIOS_OK, data);
        } catch (e) {
            Vue.$log.error("Get portfolios failed", e);
            commit(types.GET_PORTFOLIOS_FAILED);
            commit('LOADING_BY_KEY', { key: "portfolioListLoading", status: false });
        }
    },

    async buyStock({ commit, dispatch }, params) {
        let portfolioId = params.portfolioId;
        let body = params.body;
        try {
            await portfolioAPI(dispatch).buyStock(portfolioId, body);
            dispatch("getPortfolioDetail", portfolioId);
            // commit(types.BUY_STOCK_OK, data);
        } catch (e) {
            Vue.$log.error("Buy a stock failed", e);
        }
    },

    async sellStock({ commit, dispatch }, params) {
        let portfolioId = params.portfolioId;
        let body = params.body;
        try {
            await portfolioAPI(dispatch).sellStock(portfolioId, body);
            dispatch("getPortfolioDetail", portfolioId);
        } catch (e) {
            Vue.$log.error("Sell a stock failed", e);
        }
    },

    async renamePortfolio({ commit, dispatch }, params) {
        let pid = params.portfolioId;
        let body = params.body;
        try {
            await portfolioAPI(dispatch).rename(pid, body);
            dispatch("getPortfolios", pid);
        } catch (e) {
            Vue.$log.error("Rename failed", e);
        }
    },

    async getPortfolioDetail({ commit, dispatch }, portfolioId) {
        try {
            commit('LOADING_BY_KEY', { key: "portfolioDetailLoading", status: true });
            const { data } = await portfolioAPI(dispatch).getDetail(portfolioId);
            commit(types.GET_DETAIL_OK, data);
        } catch (e) {
            Vue.$log.error("Get portfolio detail failed", e);
            commit(types.GET_DETAIL_FAILED);
            commit('LOADING_BY_KEY', { key: "portfolioDetailLoading", status: false });
        }
    },

    async selectActiveTab({ commit, dispatch }, tab) {
        dispatch("getPortfolioDetail", tab.id);
        commit(types.SET_ACTIVE_TAB, tab.id);
    },

    async resetPastPrice({ commit, dispatch }) {
        commit(types.SET_PAST_PRICE);
    },
}

const mutations = {
    [types.SET_ACTIVE_TAB](state, pid) {
        const headline = state.vplist.items.find(x => x.id === pid);
        Vue.set(state, 'activeTab', headline);
    },

    [types.SET_PAST_PRICE](state, pid) {
        Vue.set(state, 'pastPrice', null);
    },

    [types.GET_PORTFOLIOS_OK](state, data) {
        // reload list when data.pid is not undefined
        if (data.pid) {
            const headline = data.items.find(x => x.id === data.pid);
            Vue.set(state, 'activeTab', headline);
        }
        Vue.set(state, 'vplist', data);
        Vue.set(state.form, 'portfolioListLoading', false);
    },

    [types.GET_PERFORMANCE_CHART_OK](state, data) {
        Vue.set(state.performanceChart.series[0], 'data', data.values);
        Vue.set(state.performanceChart.series[1], 'data', data.values1);
        Vue.set(state.performanceChart.series[2], 'data', data.values2);
        Vue.set(state.performanceChart.chartOptions, 'labels', data.dates);
        Vue.set(state.form, 'performanceChartLoading', false);
    },

    [types.GET_PRICE_HISTORY_OK](state, data) {
        Vue.set(state, 'pastPrice', data);
    },

    [types.GET_PRICE_HISTORY_FAIL](state) {
        Vue.set(state, 'pastPrice', null);
    },

    [types.GET_PORTFOLIOS_FAILED](state) {
        Vue.set(state.form, 'portfolioListLoading', false);
    },

    [types.GET_DETAIL_OK](state, data) {
        Vue.set(state, 'portfolio', data);
        Vue.set(state.form, 'portfolioDetailLoading', false);
    },

    [types.GET_DETAIL_FAILED](state) {
        Vue.set(state.form, 'portfolioDetailLoading', false);
    },

    [types.BUY_STOCK_OK](state, data) {
        if (data.code === 200) {
            Vue.set(state, 'investedOK', true);
        }
    },

    [types.SELL_STOCK_OK](state, data) {
        if (data.code === 200) {
            Vue.set(state, 'soldOK', true);
        }
    },

    [types.LOADING](state, loading) {
        Vue.set(state, 'loading', loading);
    },
    [types.LOADING_BY_KEY](state, data) {
        Vue.set(state.form, data.key, data.status);
    }
}

export default {
    namespaced: true,
    state: initState,
    getters,
    actions,
    mutations
};